import type { IntlShape, MessageDescriptor } from 'react-intl'
import type { Currency } from '@/scopes/common/domain/types'
import { useIntl as formatJsUseIntl } from 'react-intl'
import { useDependencies } from '@/scopes/common/infrastructure/dependencies'

import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'

interface FormatCurrencyOptions {
  showSign?: boolean
  currency?: Currency
}

export type Intl = IntlShape & {
  formatCurrency: (value: number, options?: FormatCurrencyOptions) => string
  formatTruncatedCurrency: (value: number, options?: FormatCurrencyOptions) => string
  formatRelativeDate: (value: Date) => string
  formatMessageIfExists: (descriptor: MessageDescriptor) => string | undefined
  defaultCurrency: Currency
}

export function useIntl(): Intl {
  const relativePastTimeResolver = useCommonDependency('relativePastTimeResolver')
  const { currencyResolver } = useDependencies()
  const intl = formatJsUseIntl()

  const defaultCurrency = currencyResolver.resolveDefaultForLocale(intl.locale)

  const formatRelativeDate = (value: Date) => {
    const { daysAgo, weeksAgo, monthsAgo } = relativePastTimeResolver.resolve(value)

    if (monthsAgo) {
      return intl.formatDate(value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }

    if (weeksAgo) {
      return intl.formatRelativeTime(-weeksAgo, 'weeks', { numeric: 'auto' })
    }

    if (daysAgo) {
      return intl.formatRelativeTime(-daysAgo, 'day', { numeric: 'auto' })
    }

    return intl.formatDate(value, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatMessageIfExists = (descriptor: MessageDescriptor): string | undefined => {
    const messages = intl.messages

    // Check if the key exists in the messages object
    if (!messages || !descriptor.id || !(descriptor.id in messages)) {
      return undefined
    }

    return intl.formatMessage(descriptor)
  }

  return {
    ...intl,
    formatCurrency: (value: number, options?: FormatCurrencyOptions) => {
      const currency = options?.currency || defaultCurrency
      const signDisplay = options?.showSign ? 'always' : 'never'
      if (currency === 'SEK') {
        const formattedCurrency = intl.formatNumber(value, {
          style: 'decimal',
          currency,
          signDisplay,
        })

        return `${formattedCurrency}:-`
      }

      return intl.formatNumber(value, {
        style: 'currency',
        currency,
        signDisplay,
      })
    },
    formatTruncatedCurrency: (value: number, options?: FormatCurrencyOptions) => {
      const currency = options?.currency || defaultCurrency
      const signDisplay = options?.showSign ? 'always' : 'never'
      if (currency === 'SEK') {
        const formattedCurrency = intl.formatNumber(value, {
          style: 'decimal',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          signDisplay,
        })

        return `${formattedCurrency}:-`
      }

      return intl.formatNumber(value, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay,
      })
    },
    formatRelativeDate,
    formatMessageIfExists,
    defaultCurrency,
  }
}
